import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { isTodayInMonth } from './dateUtils';
import './EmployeeCalendar.css';

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const isInactiveOnDay = (employee, year, month, day) => {
    if (!employee) return false;

    const dateValue = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (employee.hireDate && dateValue < employee.hireDate) {
        return true;
    }

    return Boolean(employee.terminationDate && dateValue > employee.terminationDate);
};

function EmployeeCalendar() {
    const { employeeName } = useParams();
    const [schedules, setSchedules] = useState([]);
    const [employee, setEmployee] = useState(null);
    const [currentDate, setCurrentDate] = useState(new Date());
    const captureRef = useRef(null);

    useEffect(() => {
        const fetchSchedules = () => {
            axios.get(`/api/schedules/employee/${employeeName}`, {
                params: {
                    year: currentDate.getFullYear(),
                    month: currentDate.getMonth() + 1
                }
            })
                .then((response) => {
                    setEmployee(response.data.employee);
                    const scheduleMap = response.data.schedules.reduce((acc, schedule) => {
                        acc[new Date(schedule.workDate).getDate()] = schedule.workType;
                        return acc;
                    }, {});
                    setSchedules(scheduleMap);
                })
                .catch((error) => console.error('Error fetching employee schedules:', error));
        };

        fetchSchedules();
    }, [employeeName, currentDate]);

    const handleDownload = async () => {
        const element = captureRef.current;
        if (!element) return;

        const canvas = await html2canvas(element, {
            useCORS: true,
            backgroundColor: '#ffffff',
            scale: 2,
            scrollX: 0,
            scrollY: -window.scrollY,
            width: element.scrollWidth,
            height: element.scrollHeight,
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight
        });

        const data = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const position = employee ? `(${employee.position})` : '';

        link.href = data;
        link.download = `${year}-${month} ${employeeName}${position} 스케줄.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const changeMonth = (offset) => {
        setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
    };

    const renderCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const weeks = [];
        let days = [];

        for (let i = 0; i < firstDay; i++) {
            days.push(<td key={`empty-${i}`} className="empty"></td>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const employmentInactive = isInactiveOnDay(employee, year, month, day);
            const workType = employmentInactive ? '' : schedules[day] || '';
            const todayClass = isTodayInMonth(currentDate, day) ? 'today' : '';
            days.push(
                <td key={day} className={`day ${employmentInactive ? 'employment-inactive' : ''} ${workType ? `work-type-${workType}` : ''} ${todayClass}`}>
                    <div className="day-number">{day}</div>
                    <div className="work-type-label">{workType}</div>
                </td>
            );

            if (days.length === 7) {
                weeks.push(<tr key={weeks.length}>{days}</tr>);
                days = [];
            }
        }

        if (days.length > 0) {
            while (days.length < 7) {
                days.push(<td key={`empty-end-${days.length}`} className="empty"></td>);
            }
            weeks.push(<tr key={weeks.length}>{days}</tr>);
        }

        return weeks;
    };

    const monthLabel = `${currentDate.getFullYear()}년 ${currentDate.getMonth() + 1}월`;

    return (
        <div className="employee-calendar-container">
            <header className="App-header">
                <h1 className="header-placeholder" aria-hidden="true">placeholder</h1>
                <div className="month-navigator">
                    <button onClick={() => changeMonth(-1)}>&lt;</button>
                    <span>{monthLabel}</span>
                    <button onClick={() => changeMonth(1)}>&gt;</button>
                </div>
                <button onClick={handleDownload} className="download-button">다운로드</button>
            </header>

            <div className="employee-calendar-capture" ref={captureRef}>
                <div className="employee-calendar-title">
                    <h1>{employeeName}</h1>
                    <p>{employee?.position || ''}</p>
                </div>

                <table className="employee-calendar">
                    <thead>
                        <tr>
                            {DAY_LABELS.map((label) => (
                                <th key={label}>{label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>{renderCalendar()}</tbody>
                </table>
            </div>

            <Link to="/" className="back-link">전체 스케줄로 돌아가기</Link>
        </div>
    );
}

export default EmployeeCalendar;
